import React, { useState, useEffect, Fragment } from "react";
import { 
  HiOutlineUser, 
  HiOutlineEnvelope, 
  HiOutlinePhone, 
  HiOutlineCalendar, 
  HiOutlineCamera, 
  HiOutlineIdentification,
  HiOutlineCreditCard,
  HiOutlineShieldCheck,
  HiOutlineClock
} from "react-icons/hi2";
import { FaPenToSquare } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import defaultAvatar from '../assets/smart_mentor.png';
import Api from "../service/http";

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || {});
  const [subscriptions, setSubscriptions] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedId, setExpandedId] = useState(null);

  const getProfile = async () => {
    try {
      const res = await Api.get("/web/profile");
      if (res?.data?.user) {
        localStorage.setItem("user", JSON.stringify(res?.data?.user));
        setUser(res?.data?.user);
        setSubscriptions(res?.data?.subscriptions || []);
      }
    } catch (error) {
      console.error("Failed to fetch profile", error);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusStyle = (status) => {
    const s = status?.toLowerCase();
    if (s === 'active') return 'bg-green-50 text-green-600 border-green-100';
    if (s === 'canceled' || s === 'cancelled') return 'bg-red-50 text-red-600 border-red-100';
    return 'bg-yellow-50 text-yellow-600 border-yellow-100';
  };

  return (
    <div className="flex flex-col h-full bg-gray-50/50 overflow-hidden">
      {/* Profile Header Background */}
      <div className="h-32 sm:h-48 bg-gradient-to-r from-indigo-600 to-violet-600 w-full relative flex-shrink-0">
        <div className="absolute -bottom-12 sm:-bottom-16 left-6 sm:left-10 flex items-end space-x-4 sm:space-x-6">
          <div className="relative group">
            <img
              src={user?.dp || defaultAvatar}
              alt="Profile"
              className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border-4 border-white shadow-xl bg-white"
            />
            <button className="absolute bottom-2 right-2 p-1.5 bg-indigo-600 text-white rounded-lg shadow-lg hover:bg-indigo-700 transition-all opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100">
              <HiOutlineCamera size={18} />
            </button>
          </div>
          <div className="mb-2 sm:mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 drop-shadow-sm truncate max-w-[200px] sm:max-w-md">
              {user?.first_name} {user?.last_name}
            </h1>
            <p className="text-sm sm:text-base text-gray-500 font-medium">
              {user?.role || user?.user_type || "Administrator"}
            </p>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="mt-20 sm:mt-24 px-6 sm:px-10 pb-10 flex-1 overflow-y-auto custom-scrollbar">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Stats & Quick Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Account Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-nowrap gap-2">
                  <span className="text-gray-500 text-sm">Member since</span>
                  <span className="text-gray-900 font-medium text-sm">{formatDate(user?.created_at)}</span>
                </div>
                <div className="flex items-center justify-between text-nowrap gap-2">
                  <span className="text-gray-500 text-sm">Account Type</span>
                  <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold uppercase tracking-wider border border-indigo-100">
                    {user?.user_type || "Staff"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-nowrap gap-2">
                  <span className="text-gray-500 text-sm">Status</span>
                  <div className="flex items-center space-x-1.5">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-green-600 font-bold text-sm">Active</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 gap-3">
                <button 
                  onClick={() => navigate("/profile/edit")}
                  className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 text-gray-700 w-full text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <HiOutlineIdentification size={20} />
                  </div>
                  <span className="font-semibold text-sm">Update Profile</span>
                </button>
                <button className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 text-gray-700 w-full text-left">
                  <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                    <HiOutlineShieldCheck size={20} />
                  </div>
                  <span className="font-semibold text-sm">Security Settings</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Detailed Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="flex space-x-1 p-1 bg-gray-100 rounded-xl w-fit">
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                  activeTab === "overview" 
                    ? "bg-white text-indigo-600 shadow-sm" 
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("subscriptions")}
                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                  activeTab === "subscriptions" 
                    ? "bg-white text-indigo-600 shadow-sm" 
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Subscriptions
              </button>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 min-h-[400px]">
              {activeTab === "overview" ? (
                <div className="space-y-8 animate-in fade-in duration-500">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center">
                        <span className="w-1.5 h-6 bg-indigo-600 rounded-full mr-3"></span>
                        Personal Information
                      </h3>
                      <button 
                        onClick={() => navigate("/profile/edit")}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Edit Profile"
                      >
                        <FaPenToSquare size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center">
                          <HiOutlineUser className="mr-2" /> First Name
                        </label>
                        <p className="text-gray-900 font-semibold border-b border-gray-50 pb-2">{user?.first_name || "N/A"}</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center">
                          <HiOutlineUser className="mr-2" /> Last Name
                        </label>
                        <p className="text-gray-900 font-semibold border-b border-gray-50 pb-2">{user?.last_name || "N/A"}</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center">
                          <HiOutlineEnvelope className="mr-2" /> Email Address
                        </label>
                        <p className="text-gray-900 font-semibold border-b border-gray-50 pb-2 truncate">{user?.email || "N/A"}</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center">
                          <HiOutlinePhone className="mr-2" /> Phone Number
                        </label>
                        <p className="text-gray-900 font-semibold border-b border-gray-50 pb-2">{user?.phone || "N/A"}</p>
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center">
                          <HiOutlineCalendar className="mr-2" /> Birthday
                        </label>
                        <p className="text-gray-900 font-semibold border-b border-gray-50 pb-2">{formatDate(user?.dob)}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <span className="w-1.5 h-6 bg-violet-600 rounded-full mr-3"></span>
                      Biography
                    </h3>
                    <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl italic border border-gray-100">
                      {user?.bio || "No biography provided yet. Tell us a bit about yourself to help others get to know you better."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 animate-in fade-in duration-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-1.5 h-6 bg-indigo-600 rounded-full mr-3"></span>
                    Subscription History
                  </h3>
                  
                  {subscriptions.length > 0 ? (
                    <div className="space-y-4">
                      {subscriptions.map((sub, index) => (
                        <div key={sub?.id || index} className="border border-gray-100 rounded-2xl overflow-hidden bg-white hover:shadow-md transition-shadow">
                          <div 
                            className="p-4 flex flex-wrap items-center justify-between cursor-pointer gap-4"
                            onClick={() => toggleExpand(sub?.id)}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                <HiOutlineCreditCard size={20} />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">Subscription #{index + 1}</p>
                                <p className="text-xs text-gray-400">{sub?.provider_subscription_id || sub?.id || 'N/A'}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-6">
                              <div className="hidden sm:block text-right">
                                <p className="text-xs text-gray-400 uppercase font-bold tracking-wider mb-0.5">Provider</p>
                                <p className="text-sm font-semibold text-gray-700 capitalize">{sub?.provider || 'N/A'}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusStyle(sub?.status)}`}>
                                {sub?.status || 'Unknown'}
                              </span>
                              <div className="text-gray-400">
                                {expandedId === sub?.id ? <HiOutlineClock size={20} className="rotate-180 transition-transform" /> : <HiOutlineClock size={20} />}
                              </div>
                            </div>
                          </div>

                          {expandedId === sub?.id && (
                            <div className="px-4 pb-4 pt-2 border-t border-gray-50 bg-gray-50/30 grid grid-cols-1 md:grid-cols-3 gap-4 animate-in slide-in-from-top-2 duration-300">
                              <div className="p-3">
                                <p className="text-xs text-gray-400 font-bold uppercase mb-1">Period</p>
                                <p className="text-sm font-semibold text-gray-700">{sub?.period == 1 ? "Monthly" : sub?.period == 12 ? "Yearly" : (sub?.period || 'N/A')}</p>
                              </div>
                              <div className="p-3">
                                <p className="text-xs text-gray-400 font-bold uppercase mb-1">Start Date</p>
                                <p className="text-sm font-semibold text-gray-700">{formatDate(sub?.current_period_start || sub?.start)}</p>
                              </div>
                              <div className="p-3">
                                <p className="text-xs text-gray-400 font-bold uppercase mb-1">End Date</p>
                                <p className="text-sm font-semibold text-gray-700">{formatDate(sub?.current_period_end || sub?.end)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                      <HiOutlineCreditCard size={48} className="mb-4 opacity-20" />
                      <p className="font-medium">No subscription data found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;